import React from 'react';
import { useStripe } from '@stripe/react-stripe-js';

import { UpdateAddOnInput } from '$/GraphQL/schema/types';
import { useUpdateAddOnMutation } from '$/GraphQL/schema/schema';

import { useOnSubscriptionChanged } from '$/hooks/useOnSubscriptionChanged';
import { logger } from '$/utils/logger';

interface UseUpdateAddOnOptions {
  onSuccess?: () => void;
}

export const useUpdateAddOn = (
  options?: UseUpdateAddOnOptions
): {
  updateAddOn: (input: UpdateAddOnInput) => Promise<void>;
  submitting: boolean;
  error?: string;
} => {
  const [error, setError] = React.useState('');
  const [submitting, setSubmitting] = React.useState(false);

  const stripe = useStripe();
  const [updateAddOnMutation] = useUpdateAddOnMutation();
  const onSubscriptionChanged = useOnSubscriptionChanged();

  const handleUpdateAddOn = React.useCallback(
    async (input: UpdateAddOnInput) => {
      const { data, errors } = await updateAddOnMutation({
        variables: {
          input,
        },
      });
      if (errors) {
        setError(errors[0].message);
        return;
      }
      const paymentIntentSecret = data?.updateAddOn?.paymentIntentSecret;
      if (paymentIntentSecret) {
        if (!stripe) {
          setError(
            `We are unable to authenticate your payment method. Please choose a different payment method and try again.  Please retry in a few seconds.`
          );
          return;
        }
        const paymentIntent = await stripe?.confirmCardPayment(paymentIntentSecret);
        if (paymentIntent.error) {
          setError(
            `We are unable to authenticate your payment method. Please choose a different payment method and try again.  Please retry in a few seconds.`
          );
          return;
        }
        await handleUpdateAddOn({
          ...input,
          paymentIntentId: paymentIntent?.paymentIntent?.id,
        });
      }
      await onSubscriptionChanged();
      options?.onSuccess?.();
    },
    [updateAddOnMutation, onSubscriptionChanged, options, stripe]
  );

  const updateAddOn = React.useCallback(
    async (input: UpdateAddOnInput) => {
      try {
        setError('');
        setSubmitting(true);
        await handleUpdateAddOn(input);
      } catch (e) {
        const error = e as Error;
        logger.error('Update add-on error', { error });
        setError('This credit card could not be charged. Please update your payment information.');
      } finally {
        setSubmitting(false);
      }
    },
    [handleUpdateAddOn]
  );

  return {
    updateAddOn,
    error,
    submitting,
  };
};
